/** @format */

import {handleColorLaminateOnChooseSubstrate, AccountContext, IfElse, If} from 'frontier-utilities';
import {Modal, ModalHeader, ModalFooter} from '@sttark/design-system';
import {SelectWithErrorHandler} from 'frontier-form';
import {Input, Button} from '@sttark/design-system';
import React, {useContext, useState} from 'react';
import NumberFormat from 'react-number-format';
import {useNavigate} from 'react-router-dom';

import {
    LAMINATE_THERMAL_GLOSS_FINISH,
    ORDER_EXPEDITED_PROCESSING,
    ORDER_STANDARD_PROCESSING,
    DISCONTINUED_SUBSTRATES,
    NO_LAMINATE_SUBSTRATES,
    DISCONTINUED_LAMINATES,
    getDiscontinuedLaminateForProductType,
    PRODUCT_TYPE_CARTON,
    PROOF_TYPES,
    SUBSTRATES,
    LAMINATES
} from 'frontier-constants';

export const ReorderDesignModal = props => {
    const {
        toggleIsShowingReorderForm,
        onFormNumberInputChange,
        isShowingReorderForm,
        handleReorderAddMore,
        handleReorderSave,
        onFormInputChange,
        isReorderSaving,
        isReorderInCart,
        onFormUpdate,
        formData,
        order
    } = props;

    const accountCtx = useContext(AccountContext);
    const navigate = useNavigate();

    const [isModalGrow, setIsModalGrow] = useState(false);
    const isAccountSettingsMismatched = () => {
        const companySettings = accountCtx.user.company_entity;
        return (
            order.is_fewest_possible_rolls !== companySettings.is_fewest_possible_rolls ||
            order.roll_max !== companySettings.roll_max
        );
    };

    const discontinuedLaminate = getDiscontinuedLaminateForProductType(
        order.laminate_id,
        order.product_type_id
    );

    const isDiscontinuedLaminate = typeof discontinuedLaminate !== 'undefined';

    const discontinuedSubstrate = DISCONTINUED_SUBSTRATES.find(
        ({id}) => id === parseInt(order.substrate_id)
    );

    const isDiscontinuedSubstrate = typeof discontinuedSubstrate !== 'undefined';

    const isNewSubstrateRequired =
        (discontinuedLaminate && discontinuedLaminate.isNewSubstrateRequired === true) ||
        isDiscontinuedSubstrate;

    const isLaminateRequired =
        (NO_LAMINATE_SUBSTRATES.indexOf(formData.substrate_id) === -1 &&
            (isDiscontinuedSubstrate || isDiscontinuedLaminate)) ||
        parseInt(order.laminate_id) === LAMINATE_THERMAL_GLOSS_FINISH;

    const substrateReplacements = discontinuedSubstrate?.replacements ?? [];

    const productQtyInputLabel =
        order.product_type_id === PRODUCT_TYPE_CARTON ? 'Carton qty' : 'Label qty';

    const canAddToCart =
        formData.qty <= 0 ||
        formData.processing_id == '' ||
        formData.proof_type_id == '' ||
        (isDiscontinuedSubstrate && !formData.substrate_id && !formData.laminate_id) ||
        (isLaminateRequired && !formData.laminate_id);

    const substrateOptions = SUBSTRATES.filter(
        ({id}) =>
            DISCONTINUED_SUBSTRATES.findIndex(({id: discontinuedId}) => discontinuedId === id) ===
            -1
    ).map(({id, label}) => ({id, label}));

    const proofTypes = [PROOF_TYPES[1], PROOF_TYPES[2], PROOF_TYPES[0]];

    const handleGrowModal = () => {
        setIsModalGrow(!isModalGrow);
    };

    return (
        <Modal isOpen={isShowingReorderForm} onClose={toggleIsShowingReorderForm} bodyHeightAuto>
            <ModalHeader>Re-order design</ModalHeader>
            <IfElse condition={isReorderInCart}>
                <div className="alert alert-success">
                    <NumberFormat
                        value={formData.qty}
                        displayType="text"
                        thousandSeparator={true}
                    />{' '}
                    added to Cart
                </div>
                <div>
                    <If condition={isNewSubstrateRequired}>
                        <>
                            <div className="alert alert-warning" style={{marginBotton: 16}}>
                                <IfElse condition={!isDiscontinuedLaminate}>
                                    <>
                                        The material you have chosen is discontinued we recommend
                                        either of the following:
                                        {substrateReplacements
                                            .map(
                                                substrateId =>
                                                    SUBSTRATES.find(({id}) => id === substrateId)
                                                        .label
                                            )
                                            .join(', ')}
                                    </>
                                    <>{discontinuedLaminate?.recommendation}</>
                                </IfElse>
                            </div>
                            <div style={{marginBottom: 16}}>
                                <SelectWithErrorHandler
                                    value={formData.substrate_id}
                                    onChange={substrateId => {
                                        const updateTarget = handleColorLaminateOnChooseSubstrate({
                                            colorId: order.color_id,
                                            substrateId: parseInt(substrateId),
                                            laminateId: order.laminate_id
                                        });
                                        onFormUpdate(updateTarget);
                                    }}
                                    className="form-control form-control-sm"
                                    placeholder={'Select a substrate'}
                                    options={substrateOptions}
                                    label="Select Substrate"
                                    newestds
                                />
                            </div>
                        </>
                    </If>
                    <If condition={isLaminateRequired}>
                        <div className="w-100" style={{marginBottom: 16}}>
                            <SelectWithErrorHandler
                                options={LAMINATES.filter(({id}) => {
                                    // Filter out thermal gloss finish
                                    if (parseInt(id) === LAMINATE_THERMAL_GLOSS_FINISH) {
                                        return false;
                                    }
                                    // Filter out discontinued laminates for this product type
                                    const discontinuedForProductType =
                                        getDiscontinuedLaminateForProductType(
                                            id,
                                            order.product_type_id
                                        );
                                    return !discontinuedForProductType;
                                })}
                                onChange={onFormInputChange.bind(this, 'laminate_id')}
                                className="form-control form-control-sm"
                                placeholder={'Select a laminate'}
                                value={formData.laminate_id}
                                label={'Select Laminate'}
                                newestds
                            />
                        </div>
                    </If>
                    <div className="w-100" style={{marginBottom: 16}}>
                        <Input
                            onChange={onFormNumberInputChange.bind(this, 'qty')}
                            style={{padding: 16, height: 65, fontSize: 16}}
                            className="form-control form-control-sm"
                            placeholder={productQtyInputLabel}
                            label={productQtyInputLabel}
                            value={formData.qty}
                            newestds
                        />
                    </div>
                    <div style={{marginBottom: 16}}>
                        <SelectWithErrorHandler
                            onChange={onFormInputChange.bind(this, 'processing_id')}
                            className="form-control form-control-sm ml-2 mr-2"
                            options={[
                                {id: ORDER_STANDARD_PROCESSING, label: 'Standard'},
                                {id: ORDER_EXPEDITED_PROCESSING, label: 'Expedited'}
                            ]}
                            value={formData.processing_id}
                            placeholder={'Select a processing time'}
                            label="Select Processing Time"
                            newestds
                        />
                    </div>
                    <div
                        onClick={handleGrowModal}
                        style={{
                            height: isModalGrow ? 200 : 'auto'
                        }}>
                        <SelectWithErrorHandler
                            onChange={onFormInputChange.bind(this, 'proof_type_id')}
                            className="form-control form-control-sm"
                            placeholder={'Select a proof type'}
                            value={formData.proof_type_id}
                            options={proofTypes.map(type => ({
                                label: type.label,
                                value: type.id,
                                id: type.id
                            }))}
                            label="Select Proof Type"
                            style={{marginBottom: 16}}
                            name="proof_type_id"
                            newestds
                        />
                    </div>
                    <If
                        condition={
                            formData.qty > 0 && formData.proof_type_id != '' && formData.qty < 10
                        }>
                        <div className="alert alert-warning" style={{marginBottom: 16}}>
                            Are you sure you only want {formData.qty} label
                            {formData.qty == 1 ? '' : 's'}?
                        </div>
                    </If>
                    <If condition={order.is_special_roll_qty && isAccountSettingsMismatched()}>
                        <div
                            className="mx-0 mt-0 mb-4 p-0 color-structure-darker"
                            style={{marginBottom: 16}}>
                            Any Custom Roll Size settings will be reset to your Default Account
                            Settings
                        </div>
                    </If>
                </div>
            </IfElse>

            <ModalFooter>
                <IfElse condition={isReorderSaving}>
                    <div className="d-flex align-items-center justify-content-end">
                        <i className="fa fa-spinner fa-spin me-2" />
                        <span>Adding to Cart...</span>
                    </div>
                    <IfElse condition={!isReorderInCart}>
                        <div className="d-flex align-items-center justify-content-end gap-4">
                            <Button onClick={toggleIsShowingReorderForm} label="Cancel" secondary />
                            <Button
                                onClick={handleReorderSave}
                                disabled={canAddToCart}
                                label="Add to Cart"
                                primary
                            />
                        </div>
                        <div>
                            <div className="d-flex align-items-center justify-content-end gap-4">
                                <Button onClick={handleReorderAddMore} label="Add more" secondary />
                                <Button
                                    onClick={() => navigate('/cart')}
                                    label="View cart"
                                    primary
                                />
                            </div>
                        </div>
                    </IfElse>
                </IfElse>
            </ModalFooter>
        </Modal>
    );
};
